import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, X, LogOut, ExternalLink, Loader2, Inbox, Ticket, CheckCircle2, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { CHURCH } from "../../lib/data";
import { ImageUpload } from "../../components/admin/ImageUpload";
import { TiersEditor } from "../../components/admin/TiersEditor";
import { ProductsTab } from "../../components/admin/ProductsTab";
import { CommerceSettingsTab } from "../../components/admin/CommerceSettingsTab";
import { PromosTab } from "../../components/admin/PromosTab";
import { DiscountsTab } from "../../components/admin/DiscountsTab";
import { OrdersTab } from "../../components/admin/OrdersTab";
import { ReportsTab } from "../../components/admin/ReportsTab";
import { StaffTab } from "../../components/admin/StaffTab";
import { PeopleTab } from "../../components/admin/PeopleTab";

const SCHEMAS = {
  services: { label: "Service Times", singular: "Service", title: (i) => i.name, sub: (i) => i.time,
    fields: [{ k: "name", l: "Service Name" }, { k: "time", l: "Time (e.g. Sundays · 10:00 AM)" }, { k: "note", l: "Location / Note" }] },
  events: { label: "Events & Tickets", singular: "Event", title: (i) => i.title, sub: (i) => `${i.date} · ${i.category}`, thumb: (i) => i.image, canDuplicate: true,
    fields: [
      { k: "title", l: "Title" }, { k: "date", l: "Date", type: "date" }, { k: "time", l: "Time" },
      { k: "end_time", l: "End Time (optional)" }, { k: "location", l: "Location" }, { k: "category", l: "Category" },
      { k: "price", l: "Base Ticket Price (USD — 0 or blank = free event)" },
      { k: "tickets_left", l: "Tickets Remaining / Capacity (blank = unlimited)" },
      { k: "max_per_order", l: "Max Tickets Per Order (blank = no limit)" },
      { k: "sales_start", l: "Ticket Sales Start (optional)", type: "date" },
      { k: "sales_end", l: "Ticket Sales End (optional)", type: "date" },
      { k: "ticket_tiers", l: "Ticket Levels (optional — Early Bird / VIP / Child / Senior / Member / Group)", type: "tiers" },
      { k: "sales_paused", l: "Temporarily pause ticket sales (event stays visible)", type: "check" },
      { k: "sold_out", l: "Mark as Sold Out", type: "check" },
      { k: "taxable", l: "Tickets are taxable", type: "check" },
      { k: "image", l: "Featured Image", type: "image" },
      { k: "desc", l: "Description", type: "textarea" },
      { k: "refund_policy", l: "Refund Policy (shown to buyers)", type: "textarea" },
      { k: "terms", l: "Terms & Conditions (optional)", type: "textarea" },
    ] },
  sermons: { label: "Sermons / Media", singular: "Sermon", title: (i) => i.title, sub: (i) => `${i.speaker} · ${i.category}`,
    fields: [{ k: "title", l: "Title" }, { k: "speaker", l: "Speaker" }, { k: "date", l: "Date", type: "date" }, { k: "scripture", l: "Scripture" }, { k: "category", l: "Category (Sermons / Bible Study / Worship / Special Events)" }, { k: "youtube", l: "YouTube/Facebook Embed URL (e.g. https://www.youtube.com/embed/ID)" }, { k: "desc", l: "Description", type: "textarea" }] },
  leaders: { label: "Leadership", singular: "Leader", title: (i) => i.name, sub: (i) => i.title, thumb: (i) => i.image,
    fields: [{ k: "name", l: "Name" }, { k: "title", l: "Church Title" }, { k: "bio", l: "Biography", type: "textarea" }, { k: "responsibilities", l: "Ministry Responsibilities" }, { k: "image", l: "Photo", type: "image" }] },
  ministries: { label: "Ministries", singular: "Ministry", title: (i) => i.name, sub: (i) => i.leader, thumb: (i) => i.image,
    fields: [{ k: "name", l: "Ministry Name" }, { k: "leader", l: "Ministry Leader" }, { k: "meeting", l: "Meeting Information" }, { k: "desc", l: "Description", type: "textarea" }, { k: "image", l: "Photo", type: "image" }] },
};

const COMMERCE_TABS = {
  store: { label: "Store Products", el: () => <ProductsTab /> },
  pricing: { label: "Pricing, Fees & Tax", el: () => <CommerceSettingsTab /> },
  promos: { label: "Promo Codes", el: () => <PromosTab /> },
  sales: { label: "Scheduled Sales", el: () => <DiscountsTab /> },
  orders: { label: "Orders", el: (role) => <OrdersTab canRefund={role === "super_admin" || role === "financial_admin"} /> },
  reports: { label: "Financial Reports", el: () => <ReportsTab /> },
  people: { label: "People / Members", el: () => <PeopleTab /> },
  staff: { label: "Staff & Roles", el: () => <StaffTab /> },
};

const ROLE_TABS = {
  super_admin: ["services", "events", "sermons", "leaders", "ministries", "store", "pricing", "promos", "sales", "orders", "reports", "people", "checkin", "submissions", "staff"],
  financial_admin: ["pricing", "promos", "sales", "orders", "reports", "people", "checkin"],
  store_manager: ["store", "promos", "sales", "orders", "checkin"],
  event_manager: ["events", "orders", "checkin"],
  checkin_staff: ["checkin"],
  membership_admin: ["people", "checkin"],
  ministry_leader: ["people", "checkin"],
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const role = user?.role || "super_admin";
  const allowed = ROLE_TABS[role] || ["checkin"];
  const [tab, setTab] = useState(allowed[0]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [subs, setSubs] = useState([]);
  const [subFilter, setSubFilter] = useState("visit");
  const [tickets, setTickets] = useState([]);
  const [eventFilter, setEventFilter] = useState("all");
  const [rosterQ, setRosterQ] = useState("");

  const isSubs = tab === "submissions";
  const isCheckin = tab === "checkin";
  const isCommerce = !!COMMERCE_TABS[tab];
  const isContent = !!SCHEMAS[tab];

  const load = async () => {
    if (isCommerce) return;
    setLoading(true);
    try {
      if (isCheckin) { const { data } = await api.get(`/paypal/tickets/orders`, { params: { q: rosterQ || undefined } }); setTickets(data); }
      else if (isSubs) { const { data } = await api.get(`/admin/submissions?type=${subFilter}`); setSubs(data); }
      else if (isContent) { const { data } = await api.get(`/content/${tab}`); setItems(data); }
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, subFilter]);
  useEffect(() => {
    if (!isCheckin) return;
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [rosterQ]);

  const checkInOne = async (code) => {
    try {
      const { data } = await api.post(`/paypal/tickets/checkin/${code}`);
      setTickets((prev) => prev.map((t) => t.ticket_code === code
        ? { ...t, checked_in: true, checked_in_at: data.checked_in_at } : t));
      toast.success(data.already_checked_in ? "Already checked in" : "Checked in");
    } catch { toast.error("Check-in failed"); }
  };

  const openNew = () => { const d = {}; SCHEMAS[tab].fields.forEach((f) => (d[f.k] = f.type === "tiers" ? [] : f.type === "check" ? false : "")); setEditing({ mode: "new", data: d }); };
  const openEdit = (item) => setEditing({ mode: "edit", data: { ...item } });

  const save = async () => {
    const { mode, data } = editing;
    try {
      if (mode === "new") await api.post(`/admin/content/${tab}`, data);
      else await api.put(`/admin/content/${tab}/${data.id}`, data);
      toast.success("Saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/content/${tab}/${item.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const duplicate = async (item) => {
    try { await api.post(`/admin/events/${item.id}/duplicate`); toast.success("Duplicated"); load(); }
    catch { toast.error("Duplicate failed"); }
  };

  const schema = SCHEMAS[tab];
  const tabLabel = (t) => SCHEMAS[t]?.label || COMMERCE_TABS[t]?.label || (t === "submissions" ? "Form Submissions" : "Event Check-In");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-[#0A0A0A]/90 backdrop-blur">
        <div className="max-w-[1300px] mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={CHURCH.logo} alt="logo" className="w-9 h-9 object-contain" />
            <span className="font-display font-extrabold tracking-tight text-sm hidden sm:block">ADMIN DASHBOARD</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5"><ExternalLink size={15} /> View Site</Link>
            <span className="text-sm text-white/40 hidden sm:block">{user?.email}{role !== "super_admin" ? ` · ${role.replace("_", " ")}` : ""}</span>
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-[#E63946] hover:border-[#E63946] transition-colors"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-5 py-8 grid lg:grid-cols-[230px_1fr] gap-8">
        <aside className="flex lg:flex-col gap-1 overflow-x-auto">
          {allowed.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`text-left whitespace-nowrap px-4 py-3 rounded-lg text-sm transition-colors ${tab === t ? "bg-[#E63946] text-white" : "text-white/60 hover:bg-white/5"}`}>{tabLabel(t)}</button>
          ))}
        </aside>

        <main>
          {isCommerce ? COMMERCE_TABS[tab].el(role) : isCheckin ? (() => {
            const filtered = eventFilter === "all" ? tickets : tickets.filter((t) => t.event_id === eventFilter);
            const evMap = {};
            tickets.forEach((t) => { if (t.event_id) evMap[t.event_id] = t.event_title; });
            const events = Object.entries(evMap);
            const sold = filtered.reduce((s, t) => s + Number(t.quantity || 1), 0);
            const inCount = filtered.filter((t) => t.checked_in).reduce((s, t) => s + Number(t.quantity || 1), 0);
            return (
              <div data-testid="checkin-roster">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                  <h1 className="font-display font-bold text-2xl">Event Check-In</h1>
                  <button onClick={load} className="text-sm text-white/60 hover:text-white flex items-center gap-1.5"><Loader2 size={14} /> Refresh</button>
                </div>
                <div className="relative mb-4">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                  <input value={rosterQ} onChange={(e) => setRosterQ(e.target.value)} data-testid="roster-search-input" placeholder="Search by name, email, phone, order # or ticket code"
                    className="w-full bg-white/5 border border-white/15 rounded-lg pl-8 pr-3 py-2.5 text-sm focus:border-[#E63946] outline-none" />
                </div>
                <div className="flex gap-2 mb-6 flex-wrap">
                  <button onClick={() => setEventFilter("all")} data-testid="checkin-event-all"
                    className={`px-4 py-2 rounded-full text-sm transition-colors ${eventFilter === "all" ? "bg-white text-[#0A0A0A]" : "border border-white/15 text-white/60 hover:border-white/40"}`}>All Events</button>
                  {events.map(([id, title]) => (
                    <button key={id} onClick={() => setEventFilter(id)}
                      className={`px-4 py-2 rounded-full text-sm transition-colors ${eventFilter === id ? "bg-white text-[#0A0A0A]" : "border border-white/15 text-white/60 hover:border-white/40"}`}>{title}</button>
                  ))}
                </div>
                {loading ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div> : (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4"><p className="text-xs text-white/40 uppercase tracking-wider">Orders</p><p className="font-display font-bold text-2xl">{filtered.length}</p></div>
                      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4"><p className="text-xs text-white/40 uppercase tracking-wider">Tickets Sold</p><p className="font-display font-bold text-2xl">{sold}</p></div>
                      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4"><p className="text-xs text-white/40 uppercase tracking-wider">Checked In</p><p className="font-display font-bold text-2xl text-emerald-400">{inCount}<span className="text-white/30 text-lg">/{sold}</span></p></div>
                    </div>
                    <div className="space-y-2">
                      {filtered.map((t) => (
                        <div key={t.ticket_code || t.id} data-testid="checkin-order" className="flex items-center gap-4 bg-white/[0.03] border border-white/10 rounded-lg p-4">
                          <Ticket size={18} className={t.checked_in ? "text-emerald-400 shrink-0" : "text-[#E63946] shrink-0"} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{t.name} <span className="text-white/30 font-normal">· {t.quantity} ticket{Number(t.quantity) > 1 ? "s" : ""} · {t.method}</span></p>
                            <p className="text-sm text-white/40 truncate">{t.email}{t.phone ? ` · ${t.phone}` : ""} · <span className="font-mono">{t.ticket_code}</span>{t.order_number ? ` · ${t.order_number}` : ""}</p>
                            {eventFilter === "all" && <p className="text-xs text-white/30 truncate mt-0.5">{t.event_title}</p>}
                          </div>
                          {t.checked_in ? (
                            <span className="inline-flex items-center gap-1.5 text-sm text-emerald-400 shrink-0" data-testid="checkin-badge"><CheckCircle2 size={16} /> Checked in</span>
                          ) : (
                            <button onClick={() => checkInOne(t.ticket_code)} data-testid="checkin-order-btn"
                              className="inline-flex items-center gap-2 rounded-full bg-[#E63946] px-4 py-2 text-sm hover:bg-[#BA1B25] transition-colors shrink-0"><CheckCircle2 size={15} /> Check In</button>
                          )}
                        </div>
                      ))}
                      {filtered.length === 0 && <div className="py-16 text-center text-white/40"><Ticket className="mx-auto mb-3" /><p>No ticket orders found.</p></div>}
                    </div>
                  </>
                )}
              </div>
            );
          })() : isContent ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <h1 className="font-display font-bold text-2xl">{schema.label}</h1>
                <button onClick={openNew} data-testid="add-item-btn" className="inline-flex items-center gap-2 rounded-full bg-[#E63946] px-5 py-2.5 text-sm hover:bg-[#BA1B25] transition-colors"><Plus size={16} /> Add {schema.singular}</button>
              </div>
              {loading ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div> : (
                <div className="space-y-2">
                  {items.map((item) => (
                    <div key={item.id} data-testid="admin-item" className="flex items-center gap-4 bg-white/[0.03] border border-white/10 rounded-lg p-3 hover:border-white/25 transition-colors">
                      {schema.thumb && (
                        <div className="w-14 h-14 rounded-md overflow-hidden bg-white/5 shrink-0">
                          {schema.thumb(item) ? <img src={resolveImg(schema.thumb(item))} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{schema.title(item)}</p>
                        <p className="text-sm text-white/40 truncate">{schema.sub(item)}</p>
                      </div>
                      {schema.canDuplicate && <button onClick={() => duplicate(item)} data-testid="duplicate-event-btn" title="Duplicate" className="grid place-items-center w-9 h-9 rounded-full hover:bg-white/10"><Copy size={15} /></button>}
                      <button onClick={() => openEdit(item)} data-testid="edit-item-btn" className="grid place-items-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors"><Pencil size={15} /></button>
                      <button onClick={() => remove(item)} data-testid="delete-item-btn" className="grid place-items-center w-9 h-9 rounded-full hover:bg-[#E63946] transition-colors"><Trash2 size={15} /></button>
                    </div>
                  ))}
                  {items.length === 0 && <p className="text-white/40 py-16 text-center">No items yet. Click "Add {schema.singular}".</p>}
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className="font-display font-bold text-2xl mb-6">Form Submissions</h1>
              <div className="flex gap-2 mb-6">
                {[["visit", "Visitors"], ["contact", "Contact"], ["prayer", "Prayer Requests"]].map(([v, l]) => (
                  <button key={v} onClick={() => setSubFilter(v)} data-testid={`sub-filter-${v}`} className={`px-4 py-2 rounded-full text-sm transition-colors ${subFilter === v ? "bg-white text-[#0A0A0A]" : "border border-white/15 text-white/60 hover:border-white/40"}`}>{l}</button>
                ))}
              </div>
              {loading ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div> : (
                <div className="space-y-3">
                  {subs.map((s) => (
                    <div key={s.id} className="bg-white/[0.03] border border-white/10 rounded-lg p-5">
                      <div className="flex justify-between items-start gap-4 mb-2">
                        <p className="font-medium">{s.first_name ? `${s.first_name} ${s.last_name}` : s.name}</p>
                        <span className="text-xs text-white/40 shrink-0">{new Date(s.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-white/50 mb-2">{s.email}{s.phone ? ` · ${s.phone}` : ""}</p>
                      {s.subject && <p className="text-sm text-[#E63946] mb-1">{s.subject}</p>}
                      {s.message && <p className="text-sm text-white/70">{s.message}</p>}
                      {s.request && <p className="text-sm text-white/70">{s.request}{s.keep_private ? " · (Private)" : ""}</p>}
                      {s.type === "visit" && <p className="text-sm text-white/70">Adults: {s.adults}, Children: {s.children}{s.preferred_service ? ` · ${s.preferred_service}` : ""}{s.comments ? ` · ${s.comments}` : ""}</p>}
                    </div>
                  ))}
                  {subs.length === 0 && <div className="py-16 text-center text-white/40"><Inbox className="mx-auto mb-3" /><p>No submissions yet.</p></div>}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {editing && schema && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-[#141414] border border-white/10 rounded-2xl w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="font-display font-bold text-lg">{editing.mode === "new" ? "Add" : "Edit"} {schema.singular}</h3>
              <button onClick={() => setEditing(null)} className="grid place-items-center w-9 h-9 rounded-full hover:bg-white/10"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
              {schema.fields.map((f) => (
                <div key={f.k}>
                  {f.type !== "check" && <label className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2">{f.l}</label>}
                  {f.type === "image" ? (
                    <ImageUpload value={editing.data[f.k]} onChange={(v) => setEditing({ ...editing, data: { ...editing.data, [f.k]: v } })} />
                  ) : f.type === "tiers" ? (
                    <TiersEditor value={editing.data[f.k]} onChange={(v) => setEditing({ ...editing, data: { ...editing.data, [f.k]: v } })} />
                  ) : f.type === "check" ? (
                    <label className="flex items-center gap-2.5 text-sm text-white/70 cursor-pointer select-none">
                      <input type="checkbox" checked={!!editing.data[f.k]} data-testid={`field-${f.k}`} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.k]: e.target.checked } })} className="w-4 h-4 accent-[#E63946]" />
                      {f.l}
                    </label>
                  ) : f.type === "textarea" ? (
                    <textarea rows={4} value={editing.data[f.k] || ""} data-testid={`field-${f.k}`} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.k]: e.target.value } })} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-[#E63946] outline-none resize-none" />
                  ) : (
                    <input type={f.type === "date" ? "date" : "text"} value={editing.data[f.k] || ""} data-testid={`field-${f.k}`} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.k]: e.target.value } })} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-[#E63946] outline-none" />
                  )}
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-white/10 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="rounded-full border border-white/15 px-5 py-2.5 text-sm hover:bg-white/5">Cancel</button>
              <button onClick={save} data-testid="save-item-btn" className="rounded-full bg-[#E63946] px-6 py-2.5 text-sm hover:bg-[#BA1B25] transition-colors">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
